/* eslint-disable @typescript-eslint/no-explicit-any */
import { usePointOfSaleHoursCalendar } from 'packages/blu-booking/src/data_fetching/PointOfSaleHours/PointOfSaleHoursCalendar';
import { usePointOfSaleHoursShift } from 'packages/blu-booking/src/data_fetching/PointOfSaleHours/PointOfSaleHoursShift';
import { RootState } from 'packages/blu-booking/src/store';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

interface UseSchedulerDataParams {
  selectedDate: Date;
  selectedPointOfSale: any;
}

/**
 * Gestisce il data fetching del calendario Studio:
 * - Orari lavorativi del punto vendita (calendar)
 * - Turni del punto vendita (shift)
 * - Studi filtrati per il punto vendita selezionato
 * - Stato di loading aggregato
 */
export function useSchedulerData({
  selectedDate,
  selectedPointOfSale,
}: UseSchedulerDataParams) {
  const { loadingAppointments } = useSelector(
    (state: RootState) => state.bluBookingaAppointments
  );

  const { filtersLoad, studi } = useSelector(
    (state: RootState) => state.bluBookingCalendarFilters
  );

  const {
    data: calendarPointOfSale,
    isLoading: isLoadingCalendarPointOfSale,
    isSuccess: isSuccessCalendarPointOfSale,
  } = usePointOfSaleHoursCalendar({
    selectedDate,
    view: 'Day',
    selectedUsers: undefined,
    pointOfSaleId: selectedPointOfSale?.id,
    options: {
      enabled: filtersLoad ?? false,
      refetchInterval: 300000,
    },
  });

  const { data: shiftPointOfSale, isLoading: isLoadingShiftPointOfSale } =
    usePointOfSaleHoursShift({
      selectedDate,
      view: 'Day',
      selectedUsers: undefined,
      pointOfSaleId: selectedPointOfSale?.id,
      options: {
        enabled: filtersLoad ?? false,
        refetchInterval: 300000,
      },
    });

  const getPointOfSaleStudi = useCallback(() => {
    if (studi && Object.keys(studi ?? {}).length > 0) {
      return Object.values(studi).filter(
        (studio: any) =>
          studio.pointOfSaleId === selectedPointOfSale?.id && studio.isActive
      );
    }
    return [];
  }, [selectedPointOfSale?.id, studi]);

  const isLoadingWorkHours = useMemo(
    () => isLoadingCalendarPointOfSale || isLoadingShiftPointOfSale,
    [isLoadingCalendarPointOfSale, isLoadingShiftPointOfSale]
  );

  const isLoading = useMemo(
    () => loadingAppointments || isLoadingWorkHours,
    [loadingAppointments, isLoadingWorkHours]
  );

  return {
    calendarPointOfSale,
    shiftPointOfSale,
    getPointOfSaleStudi,
    isLoading,
    isLoadingWorkHours,
    isLoadingCalendarPointOfSale,
    isLoadingShiftPointOfSale,
    // Orari del giorno e del punto vendita correnti arrivati: senza valore
    // differito, sono gli stessi che riceve lo Scheduler
    isSuccessCalendarPointOfSale,
  };
}
